import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';

const AuthContext = createContext(null);

const API_BASE_URL = (process.env.REACT_APP_API_URL ||
  'https://kosmoholm-api-h7h6h6e6fngce8fz.norwayeast-01.azurewebsites.net').replace(/\/api\/?$/, '');

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isMsalAuthenticated = useIsAuthenticated();
  const [localToken, setLocalToken] = useState(() => localStorage.getItem('kosmoholm-auth-token'));
  const [localUser, setLocalUser] = useState(() => {
    try {
      const u = localStorage.getItem('kosmoholm-auth-user');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  });
  const [authProvider, setAuthProvider] = useState(() => localStorage.getItem('kosmoholm-auth-provider'));
  const [providerConfig, setProviderConfig] = useState({ google: null, github: null });

  // Fetch provider configs on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const [googleRes, githubRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/auth/google/config`),
          fetch(`${API_BASE_URL}/api/auth/github/config`),
        ]);
        const google = googleRes.ok ? await googleRes.json() : { configured: false };
        const github = githubRes.ok ? await githubRes.json() : { configured: false };
        setProviderConfig({ google, github });
      } catch {
        // Config fetch failed — providers won't show
      }
    };
    fetchConfigs();
  }, []);

  const isAuthenticated = isMsalAuthenticated || !!localToken;

  const user = useMemo(() => {
    if (isMsalAuthenticated && accounts.length > 0) {
      // Same reasoning as the API client: show who you are actually signed in as,
      // not whichever account happens to be first in the cache.
      const account = instance.getActiveAccount() || accounts[0];
      return { name: account.name || account.username, email: account.username, provider: 'Microsoft' };
    }
    if (localUser) {
      return { ...localUser, provider: authProvider };
    }
    return null;
  }, [isMsalAuthenticated, accounts, localUser, authProvider, instance]);

  const loginWithGoogle = useCallback(async (credentialResponse) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: credentialResponse.credential }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Google login failed');
      }
      const data = await res.json();
      localStorage.setItem('kosmoholm-auth-token', data.token);
      localStorage.setItem('kosmoholm-auth-user', JSON.stringify(data.user));
      localStorage.setItem('kosmoholm-auth-provider', 'Google');
      setLocalToken(data.token);
      setLocalUser(data.user);
      setAuthProvider('Google');
      return data;
    } catch (err) {
      console.error('Google login error:', err);
      throw err;
    }
  }, []);

  const loginWithGitHub = useCallback(async (code) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/github`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'GitHub login failed');
      }
      const data = await res.json();
      localStorage.setItem('kosmoholm-auth-token', data.token);
      localStorage.setItem('kosmoholm-auth-user', JSON.stringify(data.user));
      localStorage.setItem('kosmoholm-auth-provider', 'GitHub');
      setLocalToken(data.token);
      setLocalUser(data.user);
      setAuthProvider('GitHub');
      return data;
    } catch (err) {
      console.error('GitHub login error:', err);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    // Clear local (Google/GitHub) state *before* handing over to MSAL: logoutRedirect
    // navigates the page away, so anything queued after it may never run.
    localStorage.removeItem('kosmoholm-auth-token');
    localStorage.removeItem('kosmoholm-auth-user');
    localStorage.removeItem('kosmoholm-auth-provider');
    setLocalToken(null);
    setLocalUser(null);
    setAuthProvider(null);

    if (authProvider === 'Microsoft' || isMsalAuthenticated) {
      // Naming the account is what actually evicts it from the cache. Without it the
      // stale entry survives sign-out and gets picked up again on the next login.
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
      instance.setActiveAccount(null);
      instance.logoutRedirect({ account }).catch(console.error);
    }
  }, [instance, authProvider, isMsalAuthenticated]);

  const value = useMemo(() => ({
    isAuthenticated,
    user,
    authProvider,
    localToken,
    isMsalAuthenticated,
    providerConfig,
    loginWithGoogle,
    loginWithGitHub,
    logout,
    msalInstance: instance,
  }), [isAuthenticated, user, authProvider, localToken, isMsalAuthenticated, providerConfig, loginWithGoogle, loginWithGitHub, logout, instance]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
